import { isCategoryId, toolName, toolsFor, type CapabilityId, type CategoryId, type ToolId } from '@dale/plans';

import type { FeatureMap, PermissionKey } from '../../api/schemas';
import type { PortalMode } from '../../lib/mode';
import type { PlanTier } from '../../lib/plan';
import { filterByPermission, withPlanAccess } from './navAccess';

/**
 * The Helios shell's map of the panel: folder tabs across the top, each with
 * the pages it holds (shown as tabs inside the sheet when there is more than
 * one), and the administration behind the Ajustes gear. Pure, so the same
 * rules the sidebar uses (plan, permission, owner-only) decide every door.
 * Every plan shows the same doors: what the plan does not include carries a
 * lock (`lock`, the plan that opens it) instead of disappearing.
 *
 * At most five folders: Inicio, Dale Agents (the agents, their reports and the
 * Bandeja), Clientes, the agenda and Caja (the counter and the money). Merging
 * tabs moves a page into another folder; it never drops one.
 */

export type NavLinkItem = {
  to: string;
  label: string;
  exact?: boolean;
  /** The plan that opens this page, when the business's plan does not include it. */
  lock?: PlanTier;
  /** The catalog capability, when the path alone does not say it (a restaurant's Menú is /servicios). */
  capability?: CapabilityId;
};

export type FolderId = 'inicio' | 'equipo' | 'clientes' | 'agenda' | 'caja' | `coach-${number}`;

export type Folder = {
  id: FolderId;
  label: string;
  links: NavLinkItem[];
};

export type NavModel = {
  folders: Folder[];
  settings: NavLinkItem[];
};

export type NavModelInput = {
  portalMode: PortalMode;
  /** The business type: each tool's name and default come from it (Herramientas). */
  category?: string;
  /** Which tools show: the owner's choices over the type's defaults. Absent, the type's defaults. */
  tools?: Readonly<Record<ToolId, boolean>>;
  plan: PlanTier | undefined;
  owner: boolean;
  coach: boolean;
  can: (key: PermissionKey) => boolean;
  onboardingDraft: boolean;
  /** The server's effective feature map; undefined until it arrives, when the plan decides. */
  features?: FeatureMap;
};

const HOME: NavLinkItem = { to: '/', label: 'Inicio', exact: true };
const CALENDAR: NavLinkItem = { to: '/calendario', label: 'Calendario' };
const BOOKINGS: NavLinkItem = { to: '/reservas', label: 'Reservas' };
const COUNTER: readonly NavLinkItem[] = [
  { to: '/cobrar', label: 'Cobrar' },
  { to: '/pagos', label: 'Pagos' },
  { to: '/historial', label: 'Historial' },
];
const COMANDAS: NavLinkItem = { to: '/comandas', label: 'Comandas' };
const MONEY: readonly NavLinkItem[] = [
  { to: '/finanzas', label: 'Finanzas' },
  { to: '/facturas', label: 'Facturas' },
];
/** Dale Agents, what prepares the work for the owner: the agents first, since their approvals wait here. */
const TEAM: readonly NavLinkItem[] = [
  { to: '/agentes', label: 'Agentes' },
  { to: '/inteligencia', label: 'Informes' },
  { to: '/bandeja', label: 'Bandeja' },
];
const ADMIN: readonly NavLinkItem[] = [
  { to: '/servicios', label: 'Servicios' },
  { to: '/agenda', label: 'Agenda' },
  { to: '/equipo', label: 'Equipo' },
  { to: '/ofertas', label: 'Ofertas' },
  { to: '/negocio', label: 'Negocio' },
];

function allowed(links: readonly NavLinkItem[], input: NavModelInput): NavLinkItem[] {
  return filterByPermission(withPlanAccess(links, input.plan, input.features), input.can, input.owner);
}

function folder(id: FolderId, label: string, links: NavLinkItem[]): Folder[] {
  return links.length > 0 ? [{ id, label, links }] : [];
}

/** Everything the agents do sits under one name, Dale Agents, whichever of its pages the business has. */
function teamFolder(links: NavLinkItem[]): Folder[] {
  return folder('equipo', 'Dale Agents', links);
}

/** A coach keeps their own short shell: their classes or agenda, plus one Caja for any counter door their profile opens. */
function coachFolders(input: NavModelInput): Folder[] {
  const own: NavLinkItem[] =
    input.portalMode === 'classes'
      ? [{ to: '/clases', label: 'Mis clases' }, BOOKINGS]
      : [{ to: '/', label: 'Mi agenda', exact: true }, { to: '/calendario', label: 'Mi calendario' }];
  const ownFolders = own.map((link, index) => ({ id: `coach-${index}` as FolderId, label: link.label, links: [link] }));
  return [...ownFolders, ...folder('caja', 'Caja', allowed(COUNTER, input))];
}

function categoryOf(input: NavModelInput): CategoryId | undefined {
  return input.category && isCategoryId(input.category) ? input.category : undefined;
}

/** The tools that show for this business: the owner's choices, else the type's defaults. */
function toolsOf(input: NavModelInput): Readonly<Record<ToolId, boolean>> {
  return input.tools ?? toolsFor(categoryOf(input), {});
}

/** A tool's name in the trade's words: Paquetes at a barbería, Grupos for a tutor. */
function named(tool: ToolId, input: NavModelInput, fallback: string): string {
  const category = categoryOf(input);
  return category ? toolName(tool, category) : fallback;
}

/**
 * The agenda folder. Every type gets the Calendario (restaurants too, since
 * 2026-10-28) and its group sessions when they are on; a studio's classes lead
 * its folder as Horario.
 */
function agendaLinks(input: NavModelInput): NavLinkItem[] {
  const tools = toolsOf(input);
  const calendar = tools.calendario && input.can('agenda') ? [CALENDAR] : [];
  if (input.portalMode === 'classes') {
    return [
      ...(tools.clases ? allowed([{ to: '/clases', label: 'Horario' }], input) : []),
      ...calendar,
      ...(input.owner ? [BOOKINGS] : []),
    ];
  }
  const sessions = tools.clases ? allowed([{ to: '/clases', label: named('clases', input, 'Clases') }], input) : [];
  return [...(input.owner ? [BOOKINGS] : []), ...calendar, ...sessions];
}

function settingsLinks(input: NavModelInput): NavLinkItem[] {
  const tools = toolsOf(input);
  const floor: NavLinkItem[] = [
    ...(tools.espacios ? [{ to: '/mesas', label: named('espacios', input, input.portalMode === 'restaurant' ? 'Mesas' : 'Espacios') }] : []),
    ...(tools.planes ? [{ to: '/planes', label: named('planes', input, 'Planes') }] : []),
  ];
  const admin = ADMIN.map((link) =>
    link.to === '/servicios' && input.portalMode === 'restaurant'
      ? { ...link, label: 'Menú', capability: 'menu' as const }
      : link,
  );
  const draft: NavLinkItem[] = input.onboardingDraft ? [{ to: '/primer-dia', label: 'Primer día' }] : [];
  // The owner picks which tools show; a teammate sees the doors the owner chose.
  const herramientas: NavLinkItem[] = input.owner ? [{ to: '/herramientas', label: 'Herramientas' }] : [];
  return allowed([...draft, ...floor, ...admin, ...herramientas], input);
}

export function buildNavModel(input: NavModelInput): NavModel {
  const model = buildFullNavModel(input);
  return input.plan === 'reservas' ? withoutLocks(model) : model;
}

/**
 * Reservations only is not the CRM (Martín, 1 Nov 2026): its owner sees the doors
 * it uses and none it would have to pay for, and a folder left empty goes away.
 */
function withoutLocks(model: NavModel): NavModel {
  const open = (links: readonly NavLinkItem[]) => links.filter((link) => !link.lock);
  return {
    folders: model.folders.map((folder) => ({ ...folder, links: open(folder.links) })).filter((folder) => folder.links.length > 0),
    settings: open(model.settings),
  };
}

function buildFullNavModel(input: NavModelInput): NavModel {
  if (input.coach) return { folders: coachFolders(input), settings: [] };
  const caja = [...COUNTER, ...(input.portalMode === 'restaurant' ? [COMANDAS] : []), ...MONEY];
  return {
    folders: [
      ...folder('inicio', 'Inicio', allowed([HOME], input)),
      ...(input.owner ? teamFolder(allowed(TEAM, input)) : []),
      ...folder('clientes', 'Clientes', allowed([{ to: '/clientes', label: 'Clientes' }], input)),
      ...folder('agenda', input.portalMode === 'classes' ? 'Clases' : 'Reservas', agendaLinks(input)),
      ...folder('caja', 'Caja', allowed(caja, input)),
    ],
    settings: settingsLinks(input),
  };
}

function matches(link: NavLinkItem, pathname: string): boolean {
  if (link.exact || link.to === '/') return pathname === link.to;
  return pathname === link.to || pathname.startsWith(`${link.to}/`);
}

/** Every page in the order the tabs show them, settings last: a new screen leans in from the side it came from. */
export function navOrder(model: NavModel): string[] {
  return [...model.folders.flatMap((folder) => folder.links.map((link) => link.to)), ...model.settings.map((link) => link.to)];
}

/** The folder whose pages include this path; settings pages belong to none. */
export function folderForPath(model: NavModel, pathname: string): Folder | undefined {
  return model.folders.find((entry) => entry.links.some((link) => matches(link, pathname)));
}

export function isSettingsPath(model: NavModel, pathname: string): boolean {
  return pathname === '/ajustes' || model.settings.some((link) => matches(link, pathname));
}
